import React, { useState, useEffect, useMemo } from "react";
import Context from "../../Context";
import { useToast, Spinner } from "../../components/ui";
import { Header } from "../../components/modules";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components";
import { FaPlus, FaCircleCheck, FaXmark } from "react-icons/fa6";
import constant, { statuses } from "../../constants";
import { MdOutlineSmartToy, MdOutlineDeleteOutline } from "react-icons/md";
import { IoMaleFemaleSharp, IoFastFoodOutline } from "react-icons/io5";
import { RiStore2Line } from "react-icons/ri";
import { FaBagShopping } from "react-icons/fa6";
import { FiShoppingCart, FiSearch, FiEdit2, FiUploadCloud } from "react-icons/fi";
import { BiMoneyWithdraw } from "react-icons/bi";
import { resizeImage, validate } from "../../util";

const PRODUCT_TYPES = [
  {
    key: "regular",
    value: "ของใช้ทั่วไป",
    icon: <FiShoppingCart className="w-4 h-4" />,
  },
  {
    key: "premium",
    value: "ของพรีเมียม",
    icon: <FaBagShopping className="w-4 h-4" />,
  },
  {
    key: "richItem",
    value: "วัตถุรวยไว",
    icon: <BiMoneyWithdraw className="w-4 h-4" />,
  },
  {
    key: "toy",
    value: "ของเล่น",
    icon: <MdOutlineSmartToy className="w-4 h-4" />,
  },
  {
    key: "beauty",
    value: "ความงาม",
    icon: <IoMaleFemaleSharp className="w-4 h-4" />,
  },
  {
    key: "food",
    value: "ของกิน",
    icon: <IoFastFoodOutline className="w-4 h-4" />,
  },
];

export const ManageProducts = () => {
  const { callApi } = Context();
  const [ogProducts, setOgProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [type, setType] = useState("regular");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    getProducts();
  }, [type]);

  const getProducts = async () => {
    setLoading(true);
    const result = await callApi(
      "products-getAll",
      {
        queries: [["type", type]],
        limit: 500,
        orderBy: ["createdAt", "desc"],
      },
      { notShowLoading: true }
    );

    const items = Array.isArray(result) ? result : [];
    setOgProducts(items);
    setLoading(false);
  };

  const filteredProducts = useMemo(() => {
    if (!searchQuery.trim()) return ogProducts;
    const query = searchQuery.toLowerCase().trim();
    return ogProducts.filter(
      (p) =>
        p.name?.toLowerCase().includes(query) ||
        p.shortId?.toLowerCase().includes(query)
    );
  }, [ogProducts, searchQuery]);

  return (
    <div className="h-fit w-full min-w-[1024px] max-w-[1600px] mx-auto pb-16">
      <Header
        header="Products"
        subHeader="จัดการสินค้า"
        icon={<RiStore2Line className="text-navy" />}
      />

      {/* Category Pills Bar */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2 pt-4 scrollbar-none">
        {PRODUCT_TYPES.map(({ key, value, icon }) => {
          const isActive = type === key;
          return (
            <button
              key={key}
              onClick={() => setType(key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md font-semibold text-sm transition-all duration-150 shrink-0 cursor-pointer border ${
                isActive
                  ? "bg-navy text-white border-navy shadow-sm"
                  : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:text-gray-900"
              }`}
            >
              {icon}
              <span>{value}</span>
            </button>
          );
        })}
      </div>

      {/* Main Table Card */}
      <div className="bg-white rounded-md shadow-md border border-gray-200 mt-4 overflow-hidden">
        {/* Table Toolbar */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-bold text-gray-800">
              รายการสินค้า
            </h2>
            <span className="text-xs font-semibold px-2 py-0.5 rounded-md bg-gray-100 text-gray-600 border border-gray-200">
              {filteredProducts.length} ชนิด
            </span>
          </div>

          <div className="flex items-center gap-3">
            <div className="relative flex items-center">
              <FiSearch className="absolute left-3 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="ค้นหาชื่อ หรือรหัสสินค้า..."
                className="pl-9 pr-3 h-10 w-64 border border-gray-300 rounded-md text-sm font-semibold focus:outline-none focus:border-navy transition-all placeholder:text-gray-400"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-2.5 text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <FaXmark className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            <button
              onClick={() =>
                setSelectedProduct({
                  type: type,
                  status: statuses.ACTIVE,
                  isShowInMarket: true,
                  isShowPointRedeem: false,
                  isHotDeal: false,
                })
              }
              className="py-2.5 px-4 bg-navy hover:bg-navy/90 text-white text-sm font-bold rounded-md shadow-sm transition-all duration-150 flex items-center gap-2 cursor-pointer shrink-0"
            >
              <FaPlus className="w-3.5 h-3.5" />
              <span>เพิ่มสินค้าใหม่</span>
            </button>
          </div>
        </div>

        {/* Product Table */}
        <Table>
          <TableHeader className="bg-gray-50 border-b border-gray-200">
            <TableRow>
              <TableHead className="text-center w-16 text-xs font-bold text-gray-600">ลำดับ</TableHead>
              <TableHead className="text-left text-xs font-bold text-gray-600">สินค้า</TableHead>
              <TableHead className="text-center text-xs font-bold text-gray-600">หมวดหมู่</TableHead>
              <TableHead className="text-center text-xs font-bold text-gray-600">ราคา (บาท)</TableHead>
              <TableHead className="text-center text-xs font-bold text-gray-600">คะแนน (แต้ม)</TableHead>
              <TableHead className="text-center text-xs font-bold text-gray-600">คงเหลือ</TableHead>
              <TableHead className="text-center text-xs font-bold text-gray-600">สถานะ</TableHead>
              <TableHead className="text-center w-28 text-xs font-bold text-gray-600">จัดการ</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8}>
                  <div className="w-full h-80 flex flex-col justify-center items-center gap-3">
                    <Spinner className="w-8 h-8" color="border-navy" />
                    <span className="text-sm font-semibold text-gray-500">กำลังโหลดรายการสินค้า...</span>
                  </div>
                </TableCell>
              </TableRow>
            ) : filteredProducts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="h-64 text-center">
                  <div className="flex flex-col items-center justify-center gap-2 text-gray-400">
                    <RiStore2Line className="w-10 h-10 stroke-1" />
                    <p className="text-sm font-semibold">ไม่พบข้อมูลสินค้าในหมวดหมู่นี้</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              filteredProducts
                .slice()
                .sort((a, b) => (Number(b.price) || 0) - (Number(a.price) || 0))
                .map((product, index) => (
                  <TableRow
                    key={product.id}
                    className="hover:bg-gray-50/80 transition-colors h-16 cursor-pointer"
                    onClick={() => setSelectedProduct(product)}
                  >
                    <TableCell className="text-center text-sm font-semibold text-gray-500">
                      {index + 1}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-md border border-gray-200 overflow-hidden bg-gray-50 flex items-center justify-center shrink-0">
                          {product.image ? (
                            <img
                              src={product.image}
                              alt={product.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <RiStore2Line className="w-5 h-5 text-gray-300" />
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-1.5 font-bold text-sm text-gray-800">
                            <span>{product.name}</span>
                            {product.isHotDeal && (
                              <span className="text-xs px-1.5 py-0.5 rounded-md bg-amber-100 text-amber-800 border border-amber-200">
                                🔥 Hot
                              </span>
                            )}
                          </div>
                          {product.shortId && (
                            <span className="text-xs font-semibold text-gray-400">
                              #{product.shortId}
                            </span>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-center text-sm font-semibold text-gray-600">
                      {PRODUCT_TYPES.find((t) => t.key === product.type)?.value || product.type || "-"}
                    </TableCell>
                    <TableCell className="text-center text-sm font-semibold text-gray-800">
                      {product.isShowInMarket ? (
                        <span>฿{(Number(product.price) || 0).toLocaleString()}</span>
                      ) : (
                        <span className="text-gray-300">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center text-sm font-semibold text-navy">
                      {product.isShowPointRedeem ? (
                        <span>{(Number(product.point) || 0).toLocaleString()} pts</span>
                      ) : (
                        <span className="text-gray-300">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {(Number(product.quantity) || 0) <= 0 ? (
                        <span className="text-xs font-bold px-2 py-0.5 rounded-md bg-rose-50 text-rose-600 border border-rose-200">
                          หมด
                        </span>
                      ) : (
                        <span className="text-sm font-semibold text-gray-700">
                          {(Number(product.quantity) || 0).toLocaleString()} ชิ้น
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {product.status === statuses.ACTIVE ? (
                        <span className="text-xs font-bold px-2.5 py-1 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200">
                          เปิดขาย
                        </span>
                      ) : (
                        <span className="text-xs font-bold px-2.5 py-1 rounded-md bg-gray-100 text-gray-600 border border-gray-300">
                          ปิดขาย
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => setSelectedProduct(product)}
                        className="p-2 text-navy hover:bg-navy/10 rounded-md transition-all cursor-pointer inline-flex items-center gap-1 text-xs font-bold"
                      >
                        <FiEdit2 className="w-3.5 h-3.5" />
                        <span>แก้ไข</span>
                      </button>
                    </TableCell>
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Product Modal */}
      {selectedProduct && (
        <ProductModal
          selectedProduct={selectedProduct}
          onClose={() => setSelectedProduct(null)}
          onSuccess={() => {
            setSelectedProduct(null);
            getProducts();
          }}
        />
      )}
    </div>
  );
};

const ProductModal = ({ selectedProduct, onClose, onSuccess }) => {
  const { callApi } = Context();
  const { toast } = useToast();
  const isEditing = Boolean(selectedProduct?.id);

  const [form, setForm] = useState({
    id: selectedProduct.id || "",
    shortId: selectedProduct.shortId || "",
    name: selectedProduct.name || "",
    type: selectedProduct.type || "regular",
    status: selectedProduct.status || statuses.ACTIVE,
    price: selectedProduct.price ?? "",
    point: selectedProduct.point ?? "",
    quantity: selectedProduct.quantity ?? "",
    detail: selectedProduct.detail || "",
    image: selectedProduct.image || "",
    isShowInMarket: selectedProduct.isShowInMarket ?? true,
    isShowPointRedeem: selectedProduct.isShowPointRedeem ?? false,
    isHotDeal: selectedProduct.isHotDeal ?? false,
  });

  const [previewImage, setPreviewImage] = useState(selectedProduct.image || "");
  const [loading, setLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const base64 = await resizeImage(file);
    if (base64) {
      setPreviewImage(URL.createObjectURL(file));
      setForm((prev) => ({ ...prev, image: base64 }));
    } else {
      toast({
        className: "bg-[#c15b78]",
        title: "รองรับเฉพาะไฟล์รูปภาพ (jpg, png) เท่านั้น",
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name?.trim()) {
      toast({
        className: "bg-[#c15b78]",
        title: "กรุณาระบุชื่อสินค้า",
      });
      return;
    }
    if (!form.image) {
      toast({
        className: "bg-[#c15b78]",
        title: "กรุณาอัปโหลดรูปภาพสินค้า",
      });
      return;
    }

    setLoading(true);
    const endpoint = isEditing ? "products-editProduct" : "products-createProduct";
    const result = await callApi(endpoint, form);
    setLoading(false);

    if (result?.status) {
      toast({
        className: "bg-[rgb(59,176,101)]",
        title: (
          <div className="flex gap-2 items-center">
            <FaCircleCheck /> {isEditing ? "อัปเดตข้อมูลสำเร็จ" : "สร้างสินค้าสำเร็จ"}
          </div>
        ),
      });
      onSuccess();
    } else {
      toast({
        className: "bg-[#c15b78]",
        title: result?.toastMessage || result?.message || "ทำรายการไม่สำเร็จ",
      });
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    const result = await callApi(
      "products-deleteProduct",
      { id: form.id },
      { notShowLoading: true }
    );
    setLoading(false);

    if (result?.status) {
      toast({
        className: "bg-[rgb(59,176,101)]",
        title: (
          <div className="flex gap-2 items-center">
            <FaCircleCheck /> ลบสินค้าสำเร็จ
          </div>
        ),
      });
      onSuccess();
    } else {
      toast({
        className: "bg-[#c15b78]",
        title: "ลบสินค้าไม่สำเร็จ",
      });
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-xs p-4 overflow-y-auto">
      <div className="bg-white rounded-md shadow-2xl w-full max-w-3xl overflow-hidden border border-gray-100 my-8 animate-in fade-in zoom-in-95 duration-150 flex flex-col">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50/60">
          <div className="flex items-center gap-2">
            <h3 className="text-xl font-bold text-gray-800">
              {isEditing ? `แก้ไขสินค้า: ${form.name}` : "เพิ่มสินค้าใหม่"}
            </h3>
            {form.isHotDeal && <span className="text-lg">🔥</span>}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-all cursor-pointer"
          >
            <FaXmark className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6 overflow-y-auto max-h-[75vh]">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Left: Image Uploader */}
            <div className="md:col-span-1 flex flex-col items-center">
              <label className="text-sm font-semibold text-gray-700 self-start mb-2">
                รูปภาพสินค้า *
              </label>
              <label
                htmlFor="productImageUpload"
                className="w-full aspect-square border-2 border-dashed border-gray-300 hover:border-navy rounded-md flex flex-col items-center justify-center relative cursor-pointer overflow-hidden bg-gray-50/50 hover:bg-navy/5 transition-all group"
              >
                {previewImage ? (
                  <>
                    <img
                      src={previewImage}
                      alt="Preview"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex flex-col items-center justify-center text-white text-xs font-semibold gap-1 transition-opacity">
                      <FiUploadCloud className="w-6 h-6" />
                      <span>เปลี่ยนรูปภาพ</span>
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center text-gray-400 gap-2 p-4 text-center">
                    <FiUploadCloud className="w-8 h-8 text-gray-400 group-hover:text-navy transition-colors" />
                    <span className="text-xs font-semibold text-gray-600">
                      คลิกเพื่ออัปโหลดรูปภาพ
                    </span>
                    <span className="text-[11px] text-gray-400">JPG หรือ PNG</span>
                  </div>
                )}
                {form.isHotDeal && (
                  <span className="absolute top-2 left-2 text-xl bg-white/90 backdrop-blur-xs rounded-md px-1.5 py-0.5 shadow-xs border border-amber-200">
                    🔥
                  </span>
                )}
              </label>
              <input
                id="productImageUpload"
                type="file"
                accept="image/jpeg, image/png"
                onChange={handleImageChange}
                className="hidden"
              />
            </div>

            {/* Right: Product Details */}
            <div className="md:col-span-2 grid grid-cols-2 gap-4">
              {/* Product Name */}
              <div className="col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  ชื่อสินค้า *
                </label>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="เช่น เสื้อยืดลายมงคล"
                  className="w-full h-10 px-3 border border-gray-300 rounded-md text-sm font-semibold focus:outline-none focus:border-navy transition-all"
                />
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  หมวดหมู่
                </label>
                <select
                  value={form.type}
                  onChange={(e) => setForm({ ...form, type: e.target.value })}
                  className="w-full h-10 px-3 border border-gray-300 rounded-md text-sm font-semibold focus:outline-none focus:border-navy transition-all bg-white cursor-pointer"
                >
                  {PRODUCT_TYPES.map((t) => (
                    <option key={t.key} value={t.key}>
                      {t.value}
                    </option>
                  ))}
                </select>
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  สถานะการขาย
                </label>
                <select
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                  className="w-full h-10 px-3 border border-gray-300 rounded-md text-sm font-semibold focus:outline-none focus:border-navy transition-all bg-white cursor-pointer"
                >
                  <option value={statuses.ACTIVE}>เปิดขาย (Active)</option>
                  <option value={statuses.INACTIVE}>ปิดขาย (Inactive)</option>
                </select>
              </div>

              {/* Price */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  ราคาขาย (บาท)
                </label>
                <input
                  type="text"
                  disabled={!form.isShowInMarket}
                  value={form.price}
                  onChange={(e) => {
                    if (validate.digitOnly.test(e.target.value)) {
                      setForm({ ...form, price: e.target.value });
                    }
                  }}
                  placeholder="0"
                  className="w-full h-10 px-3 border border-gray-300 rounded-md text-sm font-semibold focus:outline-none focus:border-navy transition-all disabled:bg-gray-100 disabled:text-gray-400"
                />
              </div>

              {/* Point */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  คะแนนที่ใช้แลก (แต้ม)
                </label>
                <input
                  type="text"
                  disabled={!form.isShowPointRedeem}
                  value={form.point}
                  onChange={(e) => {
                    if (validate.digitOnly.test(e.target.value)) {
                      setForm({ ...form, point: e.target.value });
                    }
                  }}
                  placeholder="0"
                  className="w-full h-10 px-3 border border-gray-300 rounded-md text-sm font-semibold focus:outline-none focus:border-navy transition-all disabled:bg-gray-100 disabled:text-gray-400"
                />
              </div>

              {/* Stock Quantity */}
              <div className="col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  จำนวนคงเหลือในสต็อก (ชิ้น)
                </label>
                <input
                  type="text"
                  value={form.quantity}
                  onChange={(e) => {
                    if (validate.digitOnly.test(e.target.value)) {
                      setForm({ ...form, quantity: e.target.value });
                    }
                  }}
                  placeholder="0"
                  className="w-full h-10 px-3 border border-gray-300 rounded-md text-sm font-semibold focus:outline-none focus:border-navy transition-all"
                />
              </div>

              {/* Details */}
              <div className="col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  รายละเอียดสินค้า
                </label>
                <textarea
                  rows={3}
                  value={form.detail}
                  onChange={(e) => setForm({ ...form, detail: e.target.value })}
                  placeholder="คำอธิบายสินค้า..."
                  className="w-full p-3 border border-gray-300 rounded-md text-sm font-semibold focus:outline-none focus:border-navy transition-all resize-none"
                />
              </div>

              {/* Toggles */}
              <div className="col-span-2 pt-2 border-t border-gray-200 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={form.isShowInMarket}
                    onChange={(e) => setForm({ ...form, isShowInMarket: e.target.checked })}
                    className="w-4 h-4 rounded-md text-navy focus:ring-navy cursor-pointer"
                  />
                  <span className="text-sm font-semibold text-gray-700">แสดงในร้านค้า</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={form.isShowPointRedeem}
                    onChange={(e) => setForm({ ...form, isShowPointRedeem: e.target.checked })}
                    className="w-4 h-4 rounded-md text-navy focus:ring-navy cursor-pointer"
                  />
                  <span className="text-sm font-semibold text-gray-700">ใช้คะแนนแลกได้</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={form.isHotDeal}
                    onChange={(e) => setForm({ ...form, isHotDeal: e.target.checked })}
                    className="w-4 h-4 rounded-md text-navy focus:ring-navy cursor-pointer"
                  />
                  <span className="text-sm font-semibold text-gray-700">🔥 สินค้าฮิต (Hot)</span>
                </label>
              </div>
            </div>
          </div>

          {/* Modal Footer */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-200">
            {isEditing ? (
              confirmDelete ? (
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-rose-600">ยืนยันลบ?</span>
                  <button
                    type="button"
                    disabled={loading}
                    onClick={handleDelete}
                    className="py-1.5 px-3 bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold rounded-md transition-all cursor-pointer"
                  >
                    ลบเลย
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmDelete(false)}
                    className="py-1.5 px-2 text-gray-500 hover:text-gray-700 text-xs font-semibold rounded-md transition-all cursor-pointer"
                  >
                    ยกเลิก
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirmDelete(true)}
                  className="py-2 px-3 text-rose-600 hover:bg-rose-50 border border-rose-200 text-sm font-semibold rounded-md transition-all cursor-pointer flex items-center gap-1.5"
                >
                  <MdOutlineDeleteOutline className="w-4 h-4" />
                  <span>ลบสินค้านี้</span>
                </button>
              )
            ) : (
              <div />
            )}

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="py-2.5 px-4 text-sm font-semibold text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-all cursor-pointer"
              >
                ยกเลิก
              </button>
              <button
                type="submit"
                disabled={loading}
                className="py-2.5 px-5 bg-navy hover:bg-navy/90 text-white text-sm font-bold rounded-md shadow-sm transition-all duration-150 flex items-center gap-2 cursor-pointer disabled:opacity-50"
              >
                {loading && <Spinner className="w-4 h-4" color="border-white" />}
                <span>{isEditing ? "บันทึกการแก้ไข" : "สร้างสินค้า"}</span>
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
